import { diag, diagMediaState } from '../diag';
import { claimCapture, msSinceAudioSessionActivity } from '../session';
import type {
  STTConfig,
  STTError,
  STTErrorCode,
  STTEventMap,
  STTProvider,
  STTResult,
} from './types';

/**
 * Minimal structural types for the Web Speech API. The DOM lib does not ship
 * `SpeechRecognition` types in this project, so we declare just what we use.
 */
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  onstart?: (() => void) | null;
  onaudiostart?: (() => void) | null;
  onaudioend?: (() => void) | null;
  onspeechstart?: (() => void) | null;
  onspeechend?: (() => void) | null;
  onsoundstart?: (() => void) | null;
  onsoundend?: (() => void) | null;
  onnomatch?: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<
    ArrayLike<{ transcript: string; confidence: number }> & {
      isFinal: boolean;
    }
  >;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | undefined {
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

/** iOS (incl. iPadOS posing as Mac). Web Speech behaves differently there. */
export function isIOS(): boolean {
  /* istanbul ignore next -- SSR guard; navigator is always defined in browser */
  if (typeof navigator === 'undefined') return false;
  const ua =
    navigator.userAgent ||
    /* istanbul ignore next -- userAgent is always set in browser */ '';
  return (
    /iP(ad|hone|od)/.test(ua) ||
    (navigator.platform === 'MacIntel' &&
      (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints !==
        undefined &&
      (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! >
        1)
  );
}

function mapErrorCode(raw: string): STTErrorCode {
  switch (raw) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'not-allowed';
    case 'no-speech':
      return 'no-speech';
    case 'network':
      return 'network';
    case 'aborted':
      // The user called stopListening() before any speech was captured.
      // This is a normal user-initiated cancellation, not an engine error.
      return 'aborted';
    default:
      // audio-capture, language-not-supported, and anything else unmapped.
      return 'unknown';
  }
}

// --- Module-scope recognizer (survives React remounts) ---------------------
// Lives at module scope so a MicButton remount between lesson steps doesn't tear
// it down mid-flow.
//
// iOS reuse caveat: on iOS Safari a recognition instance reliably works ONCE.
// On the next turn the reused instance opens audio (onaudiostart) but captures
// nothing (no onspeechstart) — the "works once" bug — so on iOS we discard and
// recreate the instance every turn. Desktop reuses a single instance (works
// fine, and avoids churn). See `acquireRecognition`.
//
// This provider never calls getUserMedia. On iOS the recorder opens a raw
// (voice-processing off) stream before start() and holds it for the whole turn,
// which keeps the recognizer hearing after the page was backgrounded (#254,
// speech/mic.ts). A default-constraints stream released just before start()
// collided with the recognizer, so the stream is never released mid-turn.
let sharedRecognition: SpeechRecognitionLike | null = null;
let sttDiagTurn = 0;
// Release for the current turn's audio-session capture claim (see speech/session).
let releaseCaptureClaim: (() => void) | null = null;

/** Return the audio session to playback once the recognizer is done with the mic. */
function releaseRecognitionCapture(): void {
  releaseCaptureClaim?.();
  releaseCaptureClaim = null;
}

/** Detach handlers and abort an instance so it releases the mic before discard. */
function teardownRecognition(recognition: SpeechRecognitionLike): void {
  recognition.onresult = null;
  recognition.onerror = null;
  recognition.onend = null;
  recognition.onstart = null;
  recognition.onaudiostart = null;
  recognition.onaudioend = null;
  recognition.onspeechstart = null;
  recognition.onspeechend = null;
  recognition.onsoundstart = null;
  recognition.onsoundend = null;
  recognition.onnomatch = null;
  try {
    diag('teardownRecognition: abort() old instance');
    recognition.abort();
  } catch (err) {
    diag('teardownRecognition: abort() threw', err);
  }
  releaseRecognitionCapture();
}

/**
 * Return the recognizer to use for this turn. On iOS, always a FRESH instance
 * (the previous one is torn down first) to dodge the "works once" bug; elsewhere
 * a lazily-created singleton is reused.
 */
function acquireRecognition(
  Ctor: SpeechRecognitionCtor
): SpeechRecognitionLike {
  const ios = isIOS();
  if (ios) {
    diag(
      'acquireRecognition: iOS path, hadPrev=',
      !!sharedRecognition,
      '→ creating fresh instance'
    );
    if (sharedRecognition) teardownRecognition(sharedRecognition);
    sharedRecognition = new Ctor();
  } else if (!sharedRecognition) {
    diag('acquireRecognition: desktop path, creating singleton');
    sharedRecognition = new Ctor();
  } else {
    diag('acquireRecognition: desktop path, reusing singleton');
  }
  return sharedRecognition;
}

/**
 * {@link STTProvider} backed by the browser's Web Speech API
 * (`window.SpeechRecognition` / `webkitSpeechRecognition`).
 *
 * On Safari/iOS this uses Apple's on-device engine; on Chrome it routes audio
 * to Google's servers. Either way no audio blob is uploaded to our backend.
 *
 * iOS hardening: a single shared recognition instance, `continuous=false` on
 * iOS, and the caller leaving a short "ready" gap after start before the user
 * speaks (iOS drops audio for ~300ms after `start()`). The mic stream the
 * recorder holds on iOS is ignored here (see the note above).
 */
export class WebSpeechSTTProvider implements STTProvider {
  /** The engine captures the mic itself. */
  readonly capturesAudio = false;
  private resultHandlers = new Set<STTEventMap['result']>();
  private errorHandlers = new Set<STTEventMap['error']>();
  /** Whether the current turn already produced a terminal result or error. */
  private settled = false;
  /**
   * Latest non-empty transcript this turn (interim or final). Safari often ends
   * a turn without ever delivering an `isFinal` result; on `onend` we emit this
   * buffer as the final so a captured utterance is never dropped.
   */
  private latestTranscript = '';
  private diagTurn = 0;

  isSupported(): boolean {
    // The Web Speech API is exposed on insecure origins but blocked at
    // runtime, so a secure context is required for the provider to work.
    return getSpeechRecognitionCtor() !== undefined && window.isSecureContext;
  }

  /** Point the shared recognizer's handlers at THIS provider for the turn. */
  private bindHandlers(recognition: SpeechRecognitionLike): void {
    const turn = this.diagTurn;
    recognition.onstart = (): void => diag('onstart turn=', turn);
    recognition.onaudiostart = (): void => {
      diag('onaudiostart turn=', turn);
      diagMediaState(`turn=${turn} @onaudiostart`);
    };
    recognition.onsoundstart = (): void => diag('onsoundstart turn=', turn);
    recognition.onspeechstart = (): void => diag('onspeechstart turn=', turn);
    recognition.onspeechend = (): void => diag('onspeechend turn=', turn);
    recognition.onsoundend = (): void => diag('onsoundend turn=', turn);
    recognition.onaudioend = (): void => diag('onaudioend turn=', turn);
    recognition.onnomatch = (): void => diag('onnomatch turn=', turn);

    recognition.onresult = (event): void => {
      diag(
        'onresult turn=',
        turn,
        'resultIndex=',
        event.resultIndex,
        'len=',
        event.results.length
      );
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        diag(
          'onresult detail turn=',
          turn,
          'i=',
          i,
          'isFinal=',
          result.isFinal,
          'transcript=',
          JSON.stringify(transcript)
        );
        if (transcript.trim().length > 0) this.latestTranscript = transcript;
        // Web Speech confidence is untrustworthy (Safari 0, iOS noisy) → null.
        if (result.isFinal) {
          if (transcript.trim().length === 0) {
            this.emitError({
              code: 'no-speech',
              message: "We didn't catch that — try again",
            });
            return;
          }
          this.emitResult({ transcript, confidence: null, isFinal: true });
        } else {
          this.emitResult({ transcript, confidence: null, isFinal: false });
        }
      }
    };

    recognition.onerror = (event): void => {
      diag(
        'onerror turn=',
        turn,
        'settled=',
        this.settled,
        'raw=',
        event.error
      );
      // Guard: some Chromium builds fire onerror then onend for the same turn
      // (e.g. network error + implicit no-speech). Without the guard, callers
      // would receive two error events. settled is set before the first emit.
      if (this.settled) return;
      this.settled = true;
      this.emitError({
        code: mapErrorCode(event.error),
        message: `Speech recognition error: ${event.error}`,
      });
    };

    recognition.onend = (): void => {
      diag(
        'onend turn=',
        turn,
        'settled=',
        this.settled,
        'latest=',
        JSON.stringify(this.latestTranscript)
      );
      if (!this.settled) {
        if (this.latestTranscript.trim().length > 0) {
          this.emitResult({
            transcript: this.latestTranscript,
            confidence: null,
            isFinal: true,
          });
        } else {
          this.emitError({
            code: 'no-speech',
            message: "We didn't catch that — try again",
          });
        }
      }
      // iOS keeps the mic/audio session OPEN after a continuous=false turn ends
      // (the mic indicator stays lit; a stale session can throw audio-capture
      // seconds later and starves the NEXT turn's recognizer). Force-release it
      // now — well before the next turn — by detaching + aborting this instance.
      diag('onend turn=', turn, '→ releasing recognition session');
      teardownRecognition(recognition);
      /* istanbul ignore else -- sharedRecognition is always the current instance in single-turn tests */
      if (sharedRecognition === recognition) sharedRecognition = null;
    };
  }

  startListening(config: STTConfig): Promise<void> {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      return Promise.reject(
        new Error('Web Speech API is not available in this environment')
      );
    }

    const recognition = acquireRecognition(Ctor);
    recognition.lang = config.languageCode;
    recognition.interimResults = true;
    // continuous is "completely useless on iOS" (forever-growing single result
    // / silent sessions); keep it off there, on elsewhere where macOS/Chrome
    // need it to produce results at all.
    recognition.continuous = !isIOS();
    recognition.maxAlternatives = 1;

    this.settled = false;
    this.latestTranscript = '';
    this.diagTurn = ++sttDiagTurn;
    this.bindHandlers(recognition);

    diag(
      'startListening turn=',
      this.diagTurn,
      'lang=',
      config.languageCode,
      'continuous=',
      recognition.continuous,
      'isSecureContext=',
      window.isSecureContext,
      'visibility=',
      document.visibilityState
    );
    diagMediaState(`turn=${this.diagTurn} @startListening (pre-start)`);
    // How long since the audio session was last used? A small value here often
    // correlates with the "open-but-deaf mic" bug on iOS (onaudiostart fires,
    // onspeechstart never does).
    diag(
      'startListening turn=',
      this.diagTurn,
      'msSinceLastAudioSessionActivity=',
      msSinceAudioSessionActivity()
    );

    // WebKit only lets capture start under 'play-and-record': claim it first,
    // synchronously, still inside the user's tap.
    releaseRecognitionCapture();
    releaseCaptureClaim = claimCapture(`recognition turn ${this.diagTurn}`);
    try {
      recognition.start();
      diag('recognition.start() returned turn=', this.diagTurn);
    } catch (err) {
      // Safari can throw synchronously (e.g. already started). Recover by
      // aborting and retrying once, otherwise surface the error.
      diag('recognition.start() THREW turn=', this.diagTurn, err);
      try {
        recognition.abort();
        recognition.start();
        diag('recognition.start() retry succeeded turn=', this.diagTurn);
      } catch (err2) {
        diag('recognition.start() retry THREW turn=', this.diagTurn, err2);
        releaseRecognitionCapture();
        this.emitError({
          code: 'unknown',
          message:
            err2 instanceof Error
              ? err2.message
              : /* istanbul ignore next */ String(err),
        });
      }
    }
    return Promise.resolve();
  }

  stopListening(): void {
    diag('stopListening() called turn=', this.diagTurn);
    try {
      sharedRecognition?.stop();
    } catch (err) {
      diag('stopListening() stop() threw', err);
    }
  }

  addEventListener<E extends keyof STTEventMap>(
    event: E,
    handler: STTEventMap[E]
  ): void {
    if (event === 'result') {
      this.resultHandlers.add(handler as STTEventMap['result']);
    } else {
      this.errorHandlers.add(handler as STTEventMap['error']);
    }
  }

  removeEventListener<E extends keyof STTEventMap>(
    event: E,
    handler: STTEventMap[E]
  ): void {
    if (event === 'result') {
      this.resultHandlers.delete(handler as STTEventMap['result']);
    } else {
      this.errorHandlers.delete(handler as STTEventMap['error']);
    }
  }

  dispose(): void {
    // Detach this provider from the shared recognizer but KEEP the singleton (and
    // its warm audio session) alive across remounts. Stop any in-flight turn.
    diag(
      'WebSpeech.dispose() turn=',
      this.diagTurn,
      'hasShared=',
      !!sharedRecognition
    );
    if (sharedRecognition) {
      sharedRecognition.onresult = null;
      sharedRecognition.onerror = null;
      sharedRecognition.onend = null;
      sharedRecognition.onstart = null;
      sharedRecognition.onaudiostart = null;
      sharedRecognition.onaudioend = null;
      sharedRecognition.onspeechstart = null;
      sharedRecognition.onspeechend = null;
      sharedRecognition.onsoundstart = null;
      sharedRecognition.onsoundend = null;
      sharedRecognition.onnomatch = null;
      try {
        // abort(), not stop(): iOS ignores stop() for session release.
        sharedRecognition.abort();
      } catch (err) {
        diag('WebSpeech.dispose() abort() threw', err);
      }
      releaseRecognitionCapture();
      sharedRecognition = null;
    }
    this.resultHandlers.clear();
    this.errorHandlers.clear();
  }

  private emitResult(result: STTResult): void {
    if (result.isFinal) this.settled = true;
    this.resultHandlers.forEach((handler) => handler(result));
  }

  private emitError(error: STTError): void {
    this.settled = true;
    this.errorHandlers.forEach((handler) => handler(error));
  }
}
